import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, customEvent, HLine, HStack, Spacing, VStack } from "goi_common";
import { AmenityObject, ParlorDetailProps } from "@/types/parlor";
import NextLink from "next/link";
import CompareLeadBlock from "../../CompareLeadBlock";
import { ABEnabledContainer } from "@/components/ABEnabledContainer";
import ParlorGuideCards from "../../ParlorGuideCards";
import { ParlorPriceFaqList } from "../../ParlorPriceDisclosureSection";
import { isPriceDisclosureParlor } from "@/utils/parlorPriceDisclosure";

/** \r\n, 연속 빈 줄 정리 */
const cleanTrafficText = (text: string) =>
  text
    .replace(/\r\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();

interface ClosestParlor {
  id: number;
  distance: string;
  name: string;
  isTeuer: number;
  price: number;
  image: string;
  managedType?: string;
  hasParking?: boolean;
  hasMeal?: boolean;
  binsoCount?: number;
  hasTransit?: boolean;
}

interface NearbyFacility {
  id: number;
  companyname: string;
  distance: string;
  type: string;
}

interface ParlorSeoTextSectionProps {
  parlor: ParlorDetailProps;
  closestParlors?: ClosestParlor[];
  nearbyFacilities?: NearbyFacility[];
}

export default function ParlorSeoTextSection({
  parlor,
  closestParlors,
  nearbyFacilities,
}: ParlorSeoTextSectionProps) {
  const 봉안당 = nearbyFacilities?.filter((f) => f.type === "Charnel") || [];
  const 수목장 = nearbyFacilities?.filter((f) => f.type === "NaturalBurial") || [];

  const hospitalTypes = ["대학병원", "종합병원", "병원", "의료원"];
  const isHospital = hospitalTypes.some((type) => parlor.managed_type?.includes(type));

  const nearbySections: React.ReactNode[] = [];
  if (closestParlors && closestParlors.length > 0) {
    nearbySections.push(
      <NearbyParlorsSection
        key="nearby-parlors"
        parlorName={parlor.companyname}
        closestParlors={closestParlors.slice(0, 3)}
      />,
    );
  }
  if (봉안당.length > 0) {
    nearbySections.push(
      <FacilityListSection
        key="charnel"
        title={`${parlor.companyname} 인근 봉안당(납골당)`}
        icon="icon-building-mono"
        facilities={봉안당}
      />,
    );
  }
  if (수목장.length > 0) {
    nearbySections.push(
      <FacilityListSection
        key="natural-burial"
        title={`${parlor.companyname} 인근 수목장`}
        icon="icon-tombstone-mono"
        facilities={수목장}
      />,
    );
  }

  const majorSections: React.ReactNode[] = [];
  majorSections.push(<DirectionsSection key="directions" parlor={parlor} />);
  majorSections.push(<AmenitySection key="amenity" amenities={parlor.amenities} />);
  if (isHospital) {
    majorSections.push(<LinkedHospitalSection key="hospital" parlor={parlor} />);
  }
  if (isPriceDisclosureParlor(parlor)) {
    majorSections.push(
      <SectionWrapper key="faq" title={`${parlor.companyname} 자주 묻는 질문`}>
        <ParlorPriceFaqList parlor={parlor} />
      </SectionWrapper>,
    );
  }
  // 연계 병원 다음, 주변 장례식장 앞
  majorSections.push(
    <SectionWrapper key="guides" title="장례식장 선택 시 확인하면 좋을 정보들">
      <ParlorGuideCards parlorId={Number(parlor.id)} />
    </SectionWrapper>,
  );
  if (nearbySections.length > 0) {
    // 비용비교 블록(#320 A/B, A=미노출) + 주변 장례식장 섹션을 한 슬롯으로 묶어 배치
    // (대조군에서 빈 구분선이 남지 않도록 compare/nearby를 같은 major 섹션으로)
    majorSections.push(
      <div key="nearby-block">
        <ABEnabledContainer
          experimentKey={320}
          ComponentA={<></>}
          ComponentB={
            <>
              <CompareLeadBlock
                items={closestParlors}
                parlorName={parlor.companyname}
                regionLabel={parlor.address ?? ""}
              />
              <Spacing gap={24} />
            </>
          }
        />
        <NearbyGroupSection nearbySections={nearbySections} />
      </div>,
    );
  }

  return (
    <Box padding="0 19px 40px">
      {majorSections.map((section, i) => (
        <Box key={i}>
          {i > 0 && (
            <>
              <Spacing gap={32} />
              <HLine height={8} color={colors.gray50} css={{ margin: "0 -19px" }} />
              <Spacing gap={32} />
            </>
          )}
          {section}
        </Box>
      ))}
    </Box>
  );
}

/**
 * 위치, 교통 정보. 네이버가 이 상세의 핵심을 주소와 교통 안내로 보던 흔적이 있어(site: 검색 10/8)
 * parlor.goifuneral.co.kr/directions 와 의도가 겹치지 않게 시각적 강조만 낮췄다.
 * 내용은 그대로 두고 긴 대중교통, 자가용 안내는 접어 둔다 (HTML 에는 남는다)
 */
const DirectionsSection = ({ parlor }: { parlor: ParlorDetailProps }) => {
  return (
    <SectionWrapper title="위치, 교통 정보">
      <VStack gap="12px">
        <HStack gap="8px" alignItems="center">
          <ColorIcon name="location" color={colors.gray500} size={16} />
          <p className="body_14_r" css={{ color: colors.gray700 }}>
            주소 : {parlor.address}
          </p>
        </HStack>
        <Box
          as="details"
          css={{
            padding: "12px 16px",
            borderRadius: "6px",
            background: colors.gray50,
            "& > summary": { cursor: "pointer", listStyle: "none" },
            "& > summary::-webkit-details-marker": { display: "none" },
          }}
        >
          <Box as="summary" className="caption_12_sb" css={{ color: colors.gray600 }}>
            대중교통, 자가용 안내 펼쳐보기
          </Box>
          <Box css={{ marginTop: "12px" }}>
            <p className="caption_12_sb" css={{ color: colors.gray600 }}>
              대중교통
            </p>
            <p
              className="caption_12_r"
              css={{
                color: colors.gray500,
                marginTop: "4px",
                whiteSpace: "pre-line",
                lineHeight: "18px",
              }}
            >
              {parlor.traffpublic ? cleanTrafficText(parlor.traffpublic) : "준비중입니다."}
            </p>
          </Box>
          <Box css={{ marginTop: "12px" }}>
            <p className="caption_12_sb" css={{ color: colors.gray600 }}>
              자가용
            </p>
            <p
              className="caption_12_r"
              css={{
                color: colors.gray500,
                marginTop: "4px",
                whiteSpace: "pre-line",
                lineHeight: "18px",
              }}
            >
              {parlor.traffowner ? cleanTrafficText(parlor.traffowner) : "준비중입니다."}
            </p>
          </Box>
        </Box>
        {parlor.telephone && (
          <HStack gap="8px" alignItems="center">
            <ColorIcon name="icon-call-mono" color={colors.gray500} size={16} />
            <p className="body_14_r" css={{ color: colors.gray700 }}>
              전화번호 : {parlor.telephone}
            </p>
          </HStack>
        )}
      </VStack>
    </SectionWrapper>
  );
};

/** 시설 편의 정보 (아이콘 + 텍스트 설명, 없는 항목은 회색 처리) */
const AmenitySection = ({ amenities }: { amenities: AmenityObject }) => {
  const items = [
    { icon: "funeral_restaurant", label: "원내 식당 운영", available: !!amenities.has_mealroom },
    { icon: "funeral_waiting_room", label: "유족대기실 보유", available: !!amenities.has_waitroom },
    { icon: "funeral_store", label: "매점(편의점) 이용 가능", available: !!amenities.has_super },
    {
      icon: "funeral_disabled_facility",
      label: "장애인 편의시설 구비",
      available: !!amenities.has_facilities_for_impaired,
    },
    {
      icon: "funeral_parking_slot",
      label:
        amenities.has_parkinglot && amenities.parkcnt > 0
          ? `주차장 보유 (약 ${amenities.parkcnt}대)`
          : "주차장 보유",
      available: !!amenities.has_parkinglot,
    },
  ];

  const sorted = [...items].sort((a, b) => (b.available ? 1 : 0) - (a.available ? 1 : 0));

  return (
    <SectionWrapper title="편의시설 안내">
      <HStack gap="8px" css={{ flexWrap: "wrap" }}>
        {sorted.map(({ icon, label, available }) => (
          <HStack
            key={label}
            gap="6px"
            alignItems="center"
            css={{
              padding: "6px 10px",
              borderRadius: "4px",
              background: colors.gray50,
              opacity: available ? 1 : 0.45,
            }}
          >
            <ColorIcon
              name={icon as any}
              color={available ? colors.gray500 : colors.gray300}
              size={16}
            />
            <p
              className="caption_12_r"
              css={{ color: available ? colors.gray700 : colors.gray400 }}
            >
              {label}
            </p>
          </HStack>
        ))}
      </HStack>
    </SectionWrapper>
  );
};

/** 연계 병원 정보 */
const LinkedHospitalSection = ({ parlor }: { parlor: ParlorDetailProps }) => {
  const hospitalTypes = ["대학병원", "종합병원", "병원", "의료원"];
  const isHospital = hospitalTypes.some((type) => parlor.managed_type?.includes(type));

  if (!isHospital) return null;

  return (
    <SectionWrapper title="연계 병원 정보">
      <Box
        css={{
          padding: "12px 16px",
          borderRadius: "6px",
          background: colors.gray50,
        }}
      >
        <p className="body_14_m" css={{ color: colors.gray900 }}>
          {parlor.managed_type} 소속
        </p>
        <p className="caption_12_r" css={{ color: colors.gray500, marginTop: "4px" }}>
          {parlor.address}
        </p>
      </Box>
    </SectionWrapper>
  );
};

/** 주변 장례식장 */
const NearbyParlorsSection = ({
  parlorName,
  closestParlors,
}: {
  parlorName: string;
  closestParlors: ClosestParlor[];
}) => {
  return (
    <SectionWrapper title={`${parlorName} 주변 장례식장`} icon="funeral-hall">
      <VStack gap="8px">
        {closestParlors.map((p) => (
          <NextLink
            key={p.id}
            href={`/parlor/${p.id}/`}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "parlor_detail",
                  trigger_and_target: `content-uv_nearby_parlor`,
                },
              });
            }}
          >
            <HStack
              gap="12px"
              alignItems="center"
              css={{
                padding: "12px",
                borderRadius: "6px",
                background: colors.gray50,
                cursor: "pointer",
              }}
            >
              {p.image && (
                <img
                  src={p.image}
                  alt={p.name}
                  css={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "4px",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              )}
              <VStack css={{ flex: 1 }}>
                <p className="body_14_m" css={{ color: colors.gray900 }}>
                  {p.name}
                </p>
                <p className="caption_12_r" css={{ color: colors.gray500, marginTop: "2px" }}>
                  {p.distance}km
                </p>
              </VStack>
              <ColorIcon name="arrow_right_small_v2" color={colors.gray400} size={12} />
            </HStack>
          </NextLink>
        ))}
      </VStack>
    </SectionWrapper>
  );
};

/** 인근 시설 그룹 (장례식장 + 봉안당 + 수목장) */
const NearbyGroupSection = ({ nearbySections }: { nearbySections: React.ReactNode[] }) => {
  return (
    <Box>
      {nearbySections.map((section, i) => (
        <Box key={i}>
          {i > 0 && (
            <>
              <Spacing gap={24} />
              <HLine height={1} color={colors.gray100} css={{ margin: "0" }} />
              <Spacing gap={24} />
            </>
          )}
          {section}
        </Box>
      ))}
    </Box>
  );
};

/** 장묘시설 리스트 (봉안당/수목장) */
const FacilityListSection = ({
  title,
  icon,
  facilities,
}: {
  title: string;
  icon: string;
  facilities: NearbyFacility[];
}) => {
  return (
    <SectionWrapper title={title} icon={icon}>
      <VStack gap="8px">
        {facilities.map((f) => (
          <NextLink
            key={f.id}
            href={`/facilities/${f.id}/`}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "parlor_detail",
                  trigger_and_target: `content-uv_nearby_facility`,
                },
              });
            }}
          >
            <HStack
              gap="12px"
              alignItems="center"
              css={{
                padding: "12px 16px",
                borderRadius: "6px",
                background: colors.gray50,
                cursor: "pointer",
              }}
            >
              <VStack css={{ flex: 1 }}>
                <p className="body_14_m" css={{ color: colors.gray900 }}>
                  {f.companyname}
                </p>
                <p className="caption_12_r" css={{ color: colors.gray500, marginTop: "2px" }}>
                  {f.distance}km
                </p>
              </VStack>
              <ColorIcon name="arrow_right_small_v2" color={colors.gray400} size={12} />
            </HStack>
          </NextLink>
        ))}
      </VStack>
    </SectionWrapper>
  );
};

/** 공통 섹션 래퍼 */
const SectionWrapper = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: string;
  children: React.ReactNode;
}) => {
  return (
    <Box>
      <HStack gap="6px" alignItems="center" css={{ marginBottom: "12px" }}>
        {icon && <ColorIcon name={icon as any} color={colors.gray700} size={18} />}
        <h2 className="subtitle_16_sb" css={{ color: colors.gray900 }}>
          {title}
        </h2>
      </HStack>
      {children}
    </Box>
  );
};
